import moment from 'moment'
import React, { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import arrowRightIcon from '@assets/images/arrow-right-icon.svg'
import PenIcon from '@assets/images/pen-icon.svg?react'
import localizationIcon from '@assets/images/observation-localization-icon.svg'
import CalendarIcon from '@assets/images/task-calendar-icon.svg?react'

import { CommentableType, Form, FormStatus, RemoteFile } from '@types'

import { Refresh } from '@components/Refresh'

import { mapToMarker } from '@libs/apryse/utils'

import { isLimitDateReached } from '@utils/date'

import { useFilePreviews } from '@hooks/file/file-preview.hook'

import { SelectFormStatus } from '@modules/_shared/components/SelectFormStatus'
import { TimelineActivities } from '@modules/_shared/components/TimelineActivities'
import { PdfMarkerPreview } from '@modules/_shared/components/PdfMarkerPreview'

import { FormSubmitterPreview } from '../FormSubmitterPreview'

import {
  ArrowRightIcon,
  EmptyLabel,
  FormContainer,
  IconImg,
  LocalizationContainer,
  RowContainer,
  SectionActions,
  SectionContainer,
  SectionTitle,
  SmallContainer,
  TextButtonLocalization,
  ValueContainer,
  ValueLabel,
} from './FormDetails.styled'

type FormDetailsProps = {
  form: Form
  canEditFormStatus: boolean
  canEditForm: boolean
  loading: 0 | 1 | -1
  onStatusChange: (status?: FormStatus) => void
  onEditClick: VoidFunction
  onRefresh: VoidFunction
  onFormClick: VoidFunction
  onLocalizationClick: VoidFunction
}

export const FormDetails: React.FC<FormDetailsProps> = ({
  form,
  canEditFormStatus,
  canEditForm,
  loading,
  onStatusChange,
  onEditClick,
  onRefresh,
  onFormClick,
  onLocalizationClick,
}) => {
  const { t } = useTranslation()

  const [plan, setPlan] = useState<RemoteFile>()

  const { loadFilePreviews: loadPlanPreview } = useFilePreviews({
    files: form?.projectFile?.file ? ([form?.projectFile?.file] as RemoteFile[]) : undefined,
    onFilesLoaded: ([file]) => setPlan(file as RemoteFile),
  })

  useEffect(() => {
    if (!form?.projectFile?.file) return

    loadPlanPreview()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.projectFile?.file])

  const isLimitReached = form ? isLimitDateReached(form.limitDate) && form.status != FormStatus.StatusDone : false

  return (
    <Refresh small loading={loading} onRefresh={onRefresh}>
      <RowContainer>
        <SmallContainer>
          <SectionTitle>{t('modules_project-forms_components_FormDetails_phase_date_label')}</SectionTitle>
          <ValueContainer>
            {form.phase ? (
              <ValueLabel>{form.phase.shortLabel}</ValueLabel>
            ) : (
              <EmptyLabel>{t('modules_project-forms_components_FormDetails_phase_empty')}</EmptyLabel>
            )}
          </ValueContainer>
        </SmallContainer>
        <SmallContainer>
          <SectionTitle>{t('modules_project-forms_components_FormDetails_limit_date_label')}</SectionTitle>
          <ValueContainer>
            {form.limitDate ? (
              <>
                <IconImg>
                  <CalendarIcon />
                </IconImg>
                <ValueLabel isLimitReached={isLimitReached}>{moment(form.limitDate).format('DD/MM/YYYY')} </ValueLabel>
              </>
            ) : (
              <EmptyLabel>{t('modules_project-forms_components_FormDetails_limit_date_empty')}</EmptyLabel>
            )}
          </ValueContainer>
        </SmallContainer>
        {canEditForm && (
          <SectionActions>
            <div onClick={onEditClick}>
              <PenIcon />
            </div>
          </SectionActions>
        )}
      </RowContainer>

      {plan && form.projectFilePositions && form.projectFilePositions.length > 0 && (
        <RowContainer>
          <SectionContainer>
            <SectionTitle>{t('modules_project-forms_components_FormDetails_localization_label')}</SectionTitle>
            <LocalizationContainer onClick={canEditForm ? onLocalizationClick : undefined}>
              {plan?.url && form.projectFile && (
                <>
                  <PdfMarkerPreview pdfUrl={plan.url} initialMarker={mapToMarker(form, 'form')} />
                  {canEditForm && <ArrowRightIcon src={arrowRightIcon} />}
                </>
              )}
            </LocalizationContainer>
          </SectionContainer>
        </RowContainer>
      )}

      {canEditForm && !form.projectFilePositions && onLocalizationClick && (
        <>
          <RowContainer>
            <SectionContainer>
              <SectionTitle>{t('modules_project-forms_components_FormDetails_localization_label')}</SectionTitle>
              <TextButtonLocalization type="button" onClick={onLocalizationClick}>
                <img src={localizationIcon} />
                {t('modules_project-forms_components_FormDetails_localization_button_label')}
              </TextButtonLocalization>
            </SectionContainer>
          </RowContainer>
        </>
      )}

      <RowContainer>
        <SectionContainer>
          <SectionTitle>{t('modules_project-forms_components_FormDetails_form_label')}</SectionTitle>
          <FormContainer onClick={onFormClick}>
            <FormSubmitterPreview formModel={form.formModel} />
            <ArrowRightIcon src={arrowRightIcon} />
          </FormContainer>
        </SectionContainer>
      </RowContainer>

      <RowContainer>
        <SmallContainer>
          <SectionTitle>{t('modules_project-forms_components_FormDetails_status_label')}</SectionTitle>
          <SelectFormStatus
            required
            disabled={!canEditFormStatus}
            status={form.status}
            onSelectFormStatus={onStatusChange}
          />
        </SmallContainer>
      </RowContainer>

      {form.activities.length > 0 && (
        <RowContainer>
          <SectionContainer>
            <SectionTitle>{t('modules_project-forms_components_FormDetails_activity_label')}</SectionTitle>
            <TimelineActivities activities={form.activities} comments={form.comments} type={CommentableType.Form} />
          </SectionContainer>
        </RowContainer>
      )}
    </Refresh>
  )
}
